import { useEffect, useState } from "react";
import {
  CreditCard,
  TrendingUp,
  Download,
  FileSpreadsheet,
  FileText,
  Receipt,
  CheckCircle2,
  Clock,
  XCircle,
  Ban,
  Plus,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(cents, currency = "eur") {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: (currency || "eur").toUpperCase(),
  }).format((cents || 0) / 100);
}

const STATUS_META = {
  paid: { label: "Payé", color: "#22C55E", Icon: CheckCircle2 },
  pending: { label: "En attente", color: "#F59E0B", Icon: Clock },
  failed: { label: "Échec", color: "#EF4444", Icon: XCircle },
  expired: { label: "Expiré", color: "#94A3B8", Icon: XCircle },
  refunded: { label: "Remboursé", color: "#94A3B8", Icon: Receipt },
};

export default function PaymentsAdmin() {
  const [txs, setTxs] = useState([]);
  const [members, setMembers] = useState([]);
  const [wero, setWero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ user_id: "", amount: "", label: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [all, w] = await Promise.all([
        api.get("/payments/all"),
        api.get("/payments/wero"),
      ]);
      setTxs(all.data.transactions || []);
      // Build members list from paid + unpaid
      const combined = [...(all.data.paid_users || []), ...(all.data.unpaid_users || [])];
      combined.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setMembers(combined);
      setWero(w.data);
    } finally {
      setLoading(false);
    }
  };

  const recordPayment = async () => {
    if (!form.user_id || !form.amount || !form.label.trim()) {
      toast.error("Membre, montant et libellé obligatoires");
      return;
    }
    setSaving(true);
    try {
      await api.post("/payments/record", {
        user_id: form.user_id,
        amount: Number(form.amount),
        label: form.label.trim(),
      });
      toast.success("Paiement enregistré");
      setForm({ user_id: "", amount: "", label: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cancelPending = async (sid) => {
    if (!window.confirm("Supprimer cette transaction définitivement ?")) return;
    try {
      await api.delete(`/payments/transactions/${sid}`);
      toast.success("Transaction supprimée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement…</div>;
  }

  const paidTxs = txs.filter((t) => t.payment_status === "paid");
  const totalRevenue = paidTxs.reduce((s, t) => s + (t.amount || 0), 0);
  const currency = paidTxs[0]?.currency || "eur";

  return (
    <div className="space-y-8" data-testid="payments-admin-page">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-emerald-300 mb-2 flex items-center gap-2">
          <CreditCard className="w-3 h-3" /> Administration
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
          Paiements du club
        </h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Historique des transactions effectuées par les membres.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard
          label="Encaissé"
          value={formatMoney(totalRevenue, currency)}
          color="#22C55E"
          Icon={TrendingUp}
          testid="stat-revenue"
        />
        <StatCard
          label="Paiements payés"
          value={paidTxs.length}
          color="#E5B85C"
          Icon={CheckCircle2}
          testid="stat-paid-count"
        />
        <StatCard
          label="Transactions totales"
          value={txs.length}
          color="#A855F7"
          Icon={CreditCard}
          testid="stat-txs"
        />
      </div>

      {/* Wero number reminder */}
      {wero && (
        <div className="rounded-2xl bg-slate-900 border border-emerald-500/20 p-4 flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
              Numéro Wero de l'association
            </div>
            <div className="font-mono-stat font-bold text-xl text-white tracking-wider">
              {wero.number}
            </div>
          </div>
        </div>
      )}

      {/* Manual entry form */}
      <div className="rounded-2xl bg-slate-900 border border-white/10 p-5 space-y-4">
        <div className="font-display font-bold text-lg uppercase flex items-center gap-2">
          <Plus className="w-4 h-4 text-emerald-300" /> Enregistrer un paiement Wero reçu
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
              Membre
            </label>
            <select
              data-testid="record-member"
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
            >
              <option value="">— Choisir —</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
              Montant (€)
            </label>
            <input
              data-testid="record-amount"
              type="number"
              min="0.5"
              step="0.5"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="25.00"
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm font-mono-stat font-bold"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
              Libellé
            </label>
            <input
              data-testid="record-label"
              type="text"
              maxLength={80}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Cotisation mars, Entraînement..."
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
            />
          </div>
        </div>
        <button
          data-testid="record-submit"
          onClick={recordPayment}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold uppercase tracking-wide text-xs disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer le paiement"}
        </button>
      </div>

      <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap">
          <div className="font-display font-bold text-lg uppercase">Transactions</div>
          <div className="flex gap-2 flex-wrap">
            <a
              data-testid="export-csv-all"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/export.csv`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-bold uppercase tracking-wide"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV (tout)
            </a>
            <a
              data-testid="export-csv-paid"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/export.csv?status=paid`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 text-emerald-300 text-[11px] font-bold uppercase tracking-wide"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV (payés)
            </a>
            <a
              data-testid="annual-report-pdf"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/annual-report.pdf?year=${new Date().getFullYear()}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 text-amber-300 text-[11px] font-bold uppercase tracking-wide"
              title="Rapport annuel PDF pour la clôture comptable (totaux par mois + par membre)"
            >
              <FileText className="w-3.5 h-3.5" /> Clôture {new Date().getFullYear()} (PDF)
            </a>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 border-b border-white/5">
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Membre</th>
                <th className="p-3 text-left">Libellé</th>
                <th className="p-3 text-right">Montant</th>
                <th className="p-3 text-left">Statut</th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-slate-500">
                    Aucune transaction pour le moment
                  </td>
                </tr>
              )}
              {txs.map((t) => {
                const meta = STATUS_META[t.payment_status] || STATUS_META.pending;
                return (
                  <tr
                    key={t.session_id}
                    data-testid={`admin-tx-${t.session_id}`}
                    className="border-b border-white/5 hover:bg-slate-800/40"
                  >
                    <td className="p-3 font-mono-stat text-slate-400 whitespace-nowrap">
                      {formatDate(t.created_at)}
                    </td>
                    <td className="p-3">
                      <div className="font-bold">{t.user_name || "—"}</div>
                      <div className="text-[11px] text-slate-500 font-mono-stat">
                        {t.user_email || ""}
                      </div>
                    </td>
                    <td className="p-3 text-slate-300">
                      {t.label || <span className="text-slate-600 italic">—</span>}
                    </td>
                    <td className="p-3 text-right font-mono-stat font-bold whitespace-nowrap">
                      {formatMoney(t.amount, t.currency)}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <StatusPill status={t.payment_status} meta={meta} />
                        {t.payment_status === "paid" && (
                          <a
                            data-testid={`admin-receipt-${t.session_id}`}
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/receipt/${t.session_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-slate-400 hover:text-white p-1.5 rounded bg-slate-800 hover:bg-slate-700"
                            title="Télécharger le reçu"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button
                          data-testid={`admin-cancel-tx-${t.session_id}`}
                          onClick={() => cancelPending(t.session_id)}
                          className="inline-flex items-center gap-1 text-rose-300 hover:text-rose-100 p-1.5 rounded bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40"
                          title="Supprimer cette transaction"
                        >
                          <Ban className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, meta }) {
  return (
    <span
      className="text-[10px] font-mono-stat uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap"
      style={{
        background: `${meta.color}22`,
        color: meta.color,
        border: `1px solid ${meta.color}55`,
      }}
    >
      {meta.label}
    </span>
  );
}

function StatCard({ label, value, color, Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-4 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: color }} />
      <div className="flex items-center gap-2 text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-display font-black text-2xl mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
